import type { ShiftMethodSummary } from '@/types/sale';
import { tenderLabel } from './tenderLabel';

/**
 * Flatten the shift summary's `byMethod` map into renderable tender rows.
 *
 * WHY NOT A HARDCODED LIST. The shift summary page used to read exactly three
 * keys off `byMethod` — `cash`, `e-money`, `midtrans` — and render one tile per
 * key. The backend zero-fills FOUR canonical buckets (it has grouped
 * `edc-permata` since the Permata EDC went live) and additionally surfaces any
 * non-canonical gateway it finds under its own key so the totals stay honest.
 * Everything outside those three was therefore dropped on the floor: a card
 * sale was counted in the day's gross but named nowhere, so a cashier
 * reconciling the drawer saw tiles that did not add up to the total above them
 * (#1749). Iterating the keys the API actually returned is what stops the next
 * tender from vanishing the same way.
 *
 * The canonical four are emitted first, in a fixed order, even when the API
 * omits one — a tender showing Rp 0 is information; a tender missing from the
 * screen is a question. Anything else the API returned follows in its own
 * order.
 */

/** The buckets the backend zero-fills; shown even at zero, in this order. */
const CANONICAL_TENDERS = ['cash', 'e-money', 'midtrans', 'edc-permata'] as const;

export interface ShiftTenderRow {
  /** The API's `byMethod` key — the backend `payment_gateway` value. */
  key: string;
  /** Indonesian label, shared with the receipt. */
  label: string;
  count: number;
  gross: number;
  discount: number;
}

function row(key: string, m: ShiftMethodSummary | undefined): ShiftTenderRow {
  return {
    key,
    label: tenderLabel(key) ?? key,
    count: Number(m?.count) || 0,
    gross: Number(m?.gross) || 0,
    discount: Number(m?.discount) || 0,
  };
}

export function shiftTenderRows(
  byMethod?: Record<string, ShiftMethodSummary | undefined> | null,
): ShiftTenderRow[] {
  const map = byMethod ?? {};
  const rows = CANONICAL_TENDERS.map((k) => row(k, map[k]));

  for (const key of Object.keys(map)) {
    if ((CANONICAL_TENDERS as readonly string[]).includes(key)) continue;
    const extra = row(key, map[key]);
    // An unexpected key only appears because a sale landed in it, but skip a
    // wholly empty one rather than inventing a tile for it.
    if (extra.count === 0 && extra.gross === 0) continue;
    rows.push(extra);
  }

  return rows;
}

/** Day totals across every tender rendered, so the tiles sum to the headline. */
export function shiftTenderTotals(rows: ShiftTenderRow[]): { gross: number; count: number } {
  return rows.reduce(
    (acc, r) => ({ gross: acc.gross + r.gross, count: acc.count + r.count }),
    { gross: 0, count: 0 },
  );
}
